// X59 (Grant 2026-10-19): a booking grid's card no longer scrolls up and down;
// it grows to its full height and the page scrolls, while the card scrolls
// SIDEWAYS alone when its columns don't fit. A CSS-sticky header inside that
// sideways scroller can't pin to the viewport (sticky pins to the nearest
// scroll container), so the column headers live in a separate row above the
// scroller, sticky to the page top under the site's bar, with a clip whose
// scrollLeft follows the scroller's so the headers sit over their columns.
// The time column stays sticky left inside the scroller as before. Shared by
// the member grid (/book) and the admin calendar.
import {onBeforeUnmount, watch} from 'vue';
import {autoscrollDelta, pageScrollEdges} from '~/utils/bookingDrag';

/**
 * @param scrollEl the sideways scroller (a ref)
 * @param headClip the header row's clip, whose scrollLeft follows the scroller's (a ref)
 * @param headEl the sticky header row itself, for its bottom edge while dragging (a ref)
 */
export function usePinnedGridHead(scrollEl, headClip, headEl) {
  const sync = () => {
    const s = scrollEl.value;
    const c = headClip.value;
    if (s && c && c.scrollLeft !== s.scrollLeft) c.scrollLeft = s.scrollLeft;
  };
  watch(scrollEl, (el, old) => {
    old?.removeEventListener('scroll', sync);
    el?.addEventListener('scroll', sync, {passive: true});
    sync();
  }, {immediate: true});
  onBeforeUnmount(() => scrollEl.value?.removeEventListener('scroll', sync));

  /**
   * Autoscroll while dragging near an edge: sideways moves the card, up and
   * down the window (never the card). True when something moved, so the
   * caller re-reads the pointer against the grid next frame.
   */
  const autoscroll = (x, y) => {
    const s = scrollEl.value;
    if (!s) return false;
    const edges = pageScrollEdges(s.getBoundingClientRect(), headEl.value?.getBoundingClientRect().bottom ?? 0, window.innerHeight);
    const {dx, dy} = autoscrollDelta(x, y, edges);
    if (dx) s.scrollLeft += dx;
    if (dy) window.scrollBy(0, dy);
    return !!(dx || dy);
  };
  return {sync, autoscroll};
}
